import * as yup from 'yup';

// Contact form validation schema
export const contactFormSchema = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces'),
  
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email cannot exceed 100 characters'),
  
  phone: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional()
    .matches(/^[\+]?[(]?[\d\s\-\(\)]{10,}$/, 'Please enter a valid phone number'),
  
  company: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional()
    .max(100, 'Company name cannot exceed 100 characters'),
  
  subject: yup
    .string()
    .required('Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject cannot exceed 100 characters'),
  
  message: yup
    .string()
    .required('Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message cannot exceed 1000 characters'),
  
  serviceType: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional(),
  
  budget: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional(),
  
  timeline: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional(),
  
  source: yup
    .string()
    .transform((value) => value === '' ? undefined : value)
    .optional()
});

// Newsletter subscription validation schema
export const newsletterSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email cannot exceed 100 characters')
});

// Admin login validation schema
export const adminLoginSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
});

// Blog post validation schema
export const blogPostSchema = yup.object().shape({
  title: yup
    .string()
    .required('Title is required')
    .min(5, 'Title must be at least 5 characters')
    .max(100, 'Title cannot exceed 100 characters'),
  
  slug: yup
    .string()
    .optional()
    .matches(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  
  excerpt: yup
    .string()
    .required('Excerpt is required')
    .min(20, 'Excerpt must be at least 20 characters')
    .max(300, 'Excerpt cannot exceed 300 characters'),
  
  content: yup
    .string()
    .required('Content is required')
    .min(100, 'Content must be at least 100 characters'),
  
  category: yup
    .string()
    .required('Category is required'),
  
  tags: yup
    .array()
    .of(yup.string())
    .max(10, 'Cannot have more than 10 tags'),
  
  status: yup
    .string()
    .oneOf(['draft', 'published', 'archived'], 'Invalid status'),
  
  metaTitle: yup
    .string()
    .optional()
    .max(60, 'Meta title cannot exceed 60 characters'),
  
  metaDescription: yup
    .string()
    .optional()
    .max(160, 'Meta description cannot exceed 160 characters')
});

// Pricing package validation schema
export const pricingPackageSchema = yup.object().shape({
  name: yup
    .string()
    .required('Package name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  
  description: yup
    .string()
    .required('Description is required')
    .min(20, 'Description must be at least 20 characters')
    .max(500, 'Description cannot exceed 500 characters'),
  
  price: yup
    .number()
    .required('Price is required')
    .min(0, 'Price cannot be negative')
    .max(999999, 'Price is too high'),
  
  originalPrice: yup
    .number()
    .optional()
    .min(0, 'Original price cannot be negative'),
  
  currency: yup
    .string()
    .oneOf(['USD', 'EUR', 'GBP', 'CAD'], 'Invalid currency'),
  
  billingPeriod: yup
    .string()
    .oneOf(['one-time', 'monthly', 'yearly'], 'Invalid billing period'),
  
  category: yup
    .string()
    .required('Category is required'),
  
  features: yup
    .array()
    .of(yup.string().required('Feature cannot be empty'))
    .min(1, 'At least one feature is required')
    .max(20, 'Cannot have more than 20 features')
});

// Generic error message helpers
export const getFieldError = (errors: any, fieldName: string): string | undefined => {
  return errors[fieldName]?.message;
};

export const hasFieldError = (errors: any, fieldName: string): boolean => {
  return !!errors[fieldName];
};

// Sanitization helpers
export const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '');
};

export const sanitizeHtml = (html: string): string => {
  // Basic HTML sanitization - in production, use a library like DOMPurify
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '');
};

// Validation helpers
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[(]?[\d\s\-\(\)]{10,}$/;
  return phoneRegex.test(phone);
};

export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateSlug = (slug: string): boolean => {
  const slugRegex = /^[a-z0-9-]+$/;
  return slugRegex.test(slug);
};

// Form data transformation helpers
export const generateSlugFromTitle = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (date: string | Date): string => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};